import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

// Simple accessible Typeahead component (client-side list)
export default function Typeahead({
  items = [],
  value = null,
  onChange,
  placeholder = "",
  minLength = 2,
  debounceMs = 250,
  renderItem, // (item, query) => ReactNode
  getItemKey = (it) => it.code || it.id || JSON.stringify(it),
  getItemLabel = (it) =>
    it.code ? `${it.code} - ${it.desc || ""}` : it.desc || "",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listIdRef = useRef(
    "typeahead-list-" + Math.random().toString(36).slice(2)
  );
  const [portalPos, setPortalPos] = useState(null);

  useEffect(() => {
    if (!query || query.length < minLength) {
      //setFiltered([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      const q = query.toLowerCase();
      const f = items.filter((it) => {
        const code = (it.code || "").toLowerCase();
        const desc = (it.desc || "").toLowerCase();
        return code.includes(q) || desc.includes(q);
      });
      setFiltered(f.slice(0, 50));
      setLoading(false);
      setOpen(true);
      setHighlight(0);
    }, debounceMs);
    return () => clearTimeout(t);
  }, [query, items, debounceMs, minLength]);

  useEffect(() => {
    // if value is set externally, set query label
    if (value) setQuery(getItemLabel(value));
  }, [value, getItemLabel]);

  useEffect(() => {
    // if value set externally, user isn't typing
    if (value) setIsTyping(false);
  }, [value]);

  function handleSelect(it) {
    setQuery(getItemLabel(it));
    setOpen(false);
    setIsTyping(false);
    onChange && onChange(it);
  }

  function onKeyDown(e) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (query === "") {
        setOpen(false)
        setIsTyping(false)
        onChange()
        return
      }
      const it = filtered[highlight];
      if (it) handleSelect(it);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  function updatePortalPosition() {
    const inp = inputRef.current;
    if (!inp || typeof window === "undefined") return;
    const r = inp.getBoundingClientRect();
    setPortalPos({
      top: r.bottom + window.scrollY,
      left: r.left + window.scrollX,
      width: r.width,
    });
  }

  useEffect(() => {
    if (!open) return;
    updatePortalPosition();
    const onResize = () => updatePortalPosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open]);

  // Close the dropdown when the user clicks/touches outside or focus moves away
  useEffect(() => {
    function isInside(target) {
      try {
        return (
          (inputRef.current && inputRef.current.contains(target)) ||
          (listRef.current && listRef.current.contains(target))
        );
      } catch (e) {
        return false;
      }
    }

    function onDocPointer(e) {
      const t = e.target;
      if (!isInside(t)) { 
        setOpen(false);
         if (query === "") onChange() 
      }
    }

    function onDocFocusIn(e) {
      const t = e.target;
      if (!isInside(t)) {
        setOpen(false);
        if (query === "") onChange();
        listRef.current = null
      };
    }

    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("focusin", onDocFocusIn);

    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("focusin", onDocFocusIn);
    };
  }, [onChange, query]);

  return (
    <div style={{ position: "relative", overflow: "visible" }}>
      <input
        ref={inputRef}
        placeholder={placeholder}
        value={query}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          setIsTyping(true);
          // Only clear external selection when the input is explicitly emptied
          if (v === "") onChange && onChange(null);
        }}
        onFocus={() => {
          setQuery("")
          setOpen(true)
          setFiltered(items.slice(0, 50));
          // open only when the user is typing, or when query is empty/short to show initial list
          if (isTyping || !query || query.length < minLength) {
            setOpen(true);
            if (!query || query.length < minLength)
              setFiltered(items.slice(0, 50));
          }
        }}
        onKeyDown={onKeyDown}
        aria-haspopup="listbox"
        aria-controls={listIdRef.current}
        style={{
          width: "100%",
          padding: 6,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          maxWidth: "100%",
        }}
      />
      <div style={{ position: "absolute", right: 8, top: 8 }}>
        {loading && <span>…</span>}
      </div>
      {open && portalPos && 
        typeof document !== "undefined" &&
        ReactDOM.createPortal(
          <div
            className="typeahead-box"
            id={listIdRef.current}
            role="listbox"
            ref={listRef}
            style={{
              position: "absolute",
              top: portalPos.top,
              left: portalPos.left,
              width: portalPos.width,
              zIndex: 99999,
              background: "white",
              border: "0px",
              maxHeight: 240,
              overflow: "auto",
              boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              boxSizing: "border-box",
            }}
          >
            {filtered.length === 0 ? (
              isTyping && query && query.length >= minLength ? (
                <div style={{ padding: 8 }}>No results</div>
              ) : null
            ) : (
              filtered.map((it, idx) => (
                <div
                  key={getItemKey(it)}
                  role="option"
                  aria-selected={idx === highlight}
                  onMouseDown={(e) => {
                    // use mouseDown to avoid losing focus before click
                    e.preventDefault();
                    handleSelect(it);
                  }}
                  onMouseEnter={() => setHighlight(idx)}
                  style={{
                    padding: 8,
                    background: idx === highlight ? "#f2f6fb" : "white",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "space-between",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      marginRight: 8,
                    }}
                  >
                    {renderItem ? renderItem(it, query) : getItemLabel(it)}
                  </div>
                  {it.division && (
                    <div style={{ fontSize: 12, color: "#666" }}>
                      {it.division}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>,
          document.body,
          "typeahead-box"
        )}
    </div>
  );
}
